import { useRef } from "react";
import { Notice } from "@/components/ui/notice";
import { cn } from "@/lib/utils";
import { formatDay, formatShortDate, daysBetween } from "@/lib/campaignPlanning";
import { SUGGESTED_LEAD_DAYS } from "@/lib/campaignWindows";

/**
 * The windows around the reading dates (setup guide and Edit campaign),
 * each a sentence with the number of days typed in and the date it works
 * out to, then a timeline of the whole campaign that redraws as they
 * change. Fixed once the campaign starts.
 *
 * `dates` is keyDatesFor(start, end, windows, today), or null without
 * valid dates; `today` is the school's local date.
 */
export default function CampaignWindowFields({ data, setData, errors = {}, dates, today, locked = false }) {
  // Blank while typing; the server keeps the saved value if it's sent blank.
  const set = (key) => (e) => setData(key, e.target.value === "" ? "" : Number(e.target.value));
  const anyTime = data.donation_lead_days === null;
  // Switching back to a set number of days restores the one they had.
  const lastLead = useRef(anyTime || !data.donation_lead_days ? SUGGESTED_LEAD_DAYS : data.donation_lead_days);
  const chooseAnyTime = () => {
    if (data.donation_lead_days) lastLead.current = data.donation_lead_days;
    setData("donation_lead_days", null);
  };
  const chooseDays = () => setData("donation_lead_days", lastLead.current);
  // Logging closes at 9 AM on day N, donations at the end of day M.
  const loggingOutlastsDonations = Number(data.minute_logging_days) > Number(data.donation_days_after);
  const days = (n) => (Number(n) === 1 ? "day" : "days");

  return (
    <div className="space-y-4">
      <ul className="divide-y divide-border rounded-xl border border-border">
        <WindowRow
          result={dates && (anyTime && !locked
            ? (today && dates.donationsOpen < today ? `Open since ${formatDay(dates.donationsOpen)}` : "Open now")
            : `From ${formatDay(dates.donationsOpen)}`)}
          error={errors.donation_lead_days}
        >
          Donations open
          <span role="group" aria-label="When donations open" className="mx-1.5 inline-flex h-8 items-center gap-0.5 rounded-lg bg-secondary p-0.5 align-middle">
            <ChoiceButton selected={anyTime} onClick={chooseAnyTime} disabled={locked}>any time</ChoiceButton>
            {anyTime ? (
              <ChoiceButton selected={false} onClick={chooseDays} disabled={locked}>{lastLead.current} days</ChoiceButton>
            ) : (
              <span className="inline-flex h-full items-center gap-1.5 rounded-md bg-card pl-1 pr-2.5 font-medium text-foreground shadow-sm">
                <DaysInput name="donation_lead_days" value={data.donation_lead_days} onChange={set("donation_lead_days")} max={60} disabled={locked} label="Days before reading starts that donations open" bare />
                {days(data.donation_lead_days)}
              </span>
            )}
          </span>
          before reading starts
        </WindowRow>
        <WindowRow
          result={dates && (dates.loggingClosesAtEndOfDay ? `Until 11:59 PM, ${formatDay(dates.end)}` : `Until 9 AM, ${formatDay(dates.loggingCloses)}`)}
          error={errors.minute_logging_days}
        >
          Families can log minutes for
          <DaysInput name="minute_logging_days" value={data.minute_logging_days} onChange={set("minute_logging_days")} max={14} disabled={locked} label="Days after reading ends that families can log minutes" />
          more {days(data.minute_logging_days)} after reading ends
        </WindowRow>
        <WindowRow
          result={dates && `Until 11:59 PM, ${formatDay(dates.donationsClose)}`}
          error={errors.donation_days_after}
        >
          Donations stay open for
          <DaysInput name="donation_days_after" value={data.donation_days_after} onChange={set("donation_days_after")} max={30} disabled={locked} label="Days after reading ends that donations stay open" />
          more {days(data.donation_days_after)} after reading ends
        </WindowRow>
      </ul>

      {!locked && loggingOutlastsDonations && (
        <Notice tone="warning">
          The campaign is over once donations close, so families can't log minutes after that. Keep donations open longer, or shorten minute logging.
        </Notice>
      )}

      {dates && <CampaignTimeline dates={dates} />}
    </div>
  );
}

// One window as a sentence, with the date it works out to on the right.
// On phones the sentence gets the full width, with the date underneath.
function WindowRow({ children, result, error }) {
  const errorText = Array.isArray(error) ? error[0] : error;
  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <p className="w-full text-sm leading-8 text-foreground sm:w-auto sm:min-w-0 sm:flex-1">{children}</p>
        <p className="text-sm text-muted-foreground font-tabular">{result}</p>
      </div>
      {errorText && <p className="mt-1 text-sm text-destructive">{errorText}</p>}
    </li>
  );
}

// One side of the inline "any time | 14 days" choice (a toggle button).
function ChoiceButton({ selected, onClick, disabled, children }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex h-full items-center rounded-md px-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed",
        selected ? "bg-card font-medium text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
      )}
    >
      {children}
    </button>
  );
}

// A small whole-number box that sits inside a sentence.
function DaysInput({ name, value, onChange, min = 0, max, disabled, label, bare = false }) {
  return (
    <input
      id={name}
      name={name}
      type="number"
      min={min}
      max={max}
      step="1"
      inputMode="numeric"
      value={value}
      onChange={onChange}
      disabled={disabled}
      aria-label={label}
      required
      className={cn(
        "inline-block text-center text-sm font-tabular [appearance:textfield] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
        bare ? "h-6 w-10 rounded border border-input bg-background text-foreground" : "mx-1.5 h-8 w-14 rounded-md border border-input bg-card px-2 shadow-sm"
      )}
    />
  );
}

/**
 * The whole campaign on one track, sized by days: early donations,
 * reading, the extra days for logging minutes, and late donations.
 */
function CampaignTimeline({ dates }) {
  const readingDays = daysBetween(dates.start, dates.end) + 1;
  const lead = Math.max(0, daysBetween(dates.donationsOpen, dates.start));
  // From the worked-out dates, so a box left blank mid-edit draws the
  // same timeline as the dates beside it.
  const after = Math.max(0, daysBetween(dates.end, dates.donationsClose));
  const logging = dates.loggingClosesAtEndOfDay ? 0 : Math.min(daysBetween(dates.end, dates.loggingCloses), after);

  const parts = [
    lead > 0 && { key: "early", label: "Early donations", days: lead, className: "bg-accent/50" },
    { key: "reading", label: "Reading", days: readingDays, className: "bg-primary" },
    logging > 0 && { key: "logging", label: "Last minutes logged", days: logging, className: "bg-primary/40" },
    after - logging > 0 && { key: "late", label: "Late donations", days: after - logging, className: "bg-muted-foreground/30" },
  ].filter(Boolean);
  const total = parts.reduce((n, p) => n + p.days, 0);

  return (
    <div className="space-y-2" aria-hidden="true">
      <div className="flex items-center justify-between text-xs text-muted-foreground font-tabular">
        <span>{formatShortDate(dates.donationsOpen)}</span>
        <span>{formatShortDate(dates.donationsClose)}</span>
      </div>
      <div className="flex h-2.5 gap-0.5 overflow-hidden rounded-full">
        {parts.map((part) => (
          <div key={part.key} className={cn("h-full first:rounded-l-full last:rounded-r-full", part.className)} style={{ width: `${(part.days / total) * 100}%` }} />
        ))}
      </div>
      <ul className="flex flex-wrap gap-x-5 gap-y-1 text-xs">
        {parts.map((part) => (
          <li key={part.key} className="flex items-center gap-1.5">
            <span className={cn("h-2 w-2 shrink-0 rounded-full", part.className)} />
            <span className="text-muted-foreground">{part.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
